import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Loader2, Calendar as CalendarIcon, ExternalLink } from 'lucide-react';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { getISOWeekNow } from './planning/utils';

const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const DAY_NAMES_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

/**
 * Planning → Özet (yearly heatmap + monthly calendar).
 *
 * Top:    52-week heatmap. Each square = one ISO week, colour = fill rate.
 * Below:  monthly calendar (Pzt-first) with daily totals + click-to-jump.
 *
 * Both views consume `/planning/yearly-summary?year=` so we make ONE backend
 * call regardless of how the operator navigates.
 */
export default function PlanningSummary() {
  const _now = useMemo(getISOWeekNow, []);
  const [year, setYear] = useState(_now.year);
  const [monthIdx, setMonthIdx] = useState(new Date().getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/planning/yearly-summary?year=${year}`);
        if (!cancelled) setData(res.data?.data || null);
      } catch {
        if (!cancelled) toast.error('Veri yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  const stats = useMemo(() => {
    if (!data) return { assigned: 0, missing: 0, fill: 0, sundays: 0, firms: {} };
    let assigned = 0, missing = 0, sundays = 0;
    const firms = {};
    for (const w of data.weeks) {
      assigned += w.assigned;
      missing += w.missing;
      sundays += w.sunday_assigned;
      for (const [k, v] of Object.entries(w.firms || {})) firms[k] = (firms[k] || 0) + v;
    }
    const denom = assigned + missing;
    const fill = denom ? Math.round((assigned / denom) * 1000) / 10 : 0;
    return { assigned, missing, fill, sundays, firms };
  }, [data]);

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4" data-testid="planning-summary-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-emerald-500" />
          Planlama Özeti
        </h1>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="icon" onClick={() => setYear(year - 1)} data-testid="planning-summary-prev-year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-800 text-sm font-semibold">{year}</div>
          <Button variant="outline" size="icon" onClick={() => setYear(year + 1)} data-testid="planning-summary-next-year">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 ml-2" />}
        </div>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Toplam atama" value={stats.assigned} accent="emerald" testId="planning-summary-stat-assigned" />
        <StatCard label="Boş hücre" value={stats.missing} accent="amber" testId="planning-summary-stat-missing" />
        <StatCard label="Doluluk" value={`${stats.fill}%`} accent="sky" testId="planning-summary-stat-fill" />
        <StatCard label="Pazar atamaları" value={stats.sundays} accent="violet" testId="planning-summary-stat-sunday" />
      </div>

      {/* Firm breakdown */}
      {Object.keys(stats.firms).length > 0 && (
        <Card className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Firma dağılımı:</span>
            {Object.entries(stats.firms)
              .sort((a, b) => b[1] - a[1])
              .map(([fname, n]) => {
                const denom = stats.assigned || 1;
                const pct = Math.round((n / denom) * 1000) / 10;
                return (
                  <Badge key={fname} variant="outline" className="text-xs" data-testid={`planning-summary-firm-${fname}`}>
                    <span className="font-bold">{fname}</span>
                    <span className="ml-1 text-slate-500">{n} · {pct}%</span>
                  </Badge>
                );
              })}
          </div>
        </Card>
      )}

      {/* 52-week heatmap */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 text-sm font-semibold flex items-center justify-between">
          <span>Yıllık ısı haritası ({data?.weeks?.length || 0} hafta)</span>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span>Boş</span>
            {[0, 20, 40, 60, 80, 100].map((p) => (
              <span key={p} className={`inline-block h-3 w-3 rounded ${heatColor(p)}`} title={`${p}%`} />
            ))}
            <span>Dolu</span>
          </div>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-13 gap-1.5" style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }} data-testid="planning-summary-heatmap">
            {(data?.weeks || []).map((w) => (
              <Link
                key={w.week}
                to={`/dashboard/planning?year=${w.year}&week=${w.week}`}
                className={`group relative aspect-square rounded ${heatColor(w.fill_rate)} hover:ring-2 hover:ring-emerald-400 hover:scale-110 transition-all flex items-center justify-center`}
                title={`W${w.week} (${w.start_date} → ${w.end_date}) — ${w.assigned} atama, ${w.fill_rate}% doluluk`}
                data-testid={`planning-summary-week-${w.week}`}
              >
                <span className="text-[9px] font-bold text-slate-700 dark:text-slate-200 opacity-60 group-hover:opacity-100">
                  {w.week}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </Card>

      {/* Monthly calendar */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonthIdx((m) => (m + 11) % 12)} data-testid="planning-summary-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold w-32 text-center">{MONTHS_TR[monthIdx]} {year}</span>
          <Button variant="outline" size="icon" onClick={() => setMonthIdx((m) => (m + 1) % 12)} data-testid="planning-summary-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Link
            to={`/dashboard/planning?year=${year}`}
            className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
          >
            Planlama tahtasına git <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        <MonthGrid year={year} month={monthIdx} days={data?.days || {}} />
      </Card>
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  const cls = {
    emerald: 'text-emerald-600 border-emerald-200 bg-emerald-50/40',
    amber: 'text-amber-700 border-amber-200 bg-amber-50/40',
    sky: 'text-sky-700 border-sky-200 bg-sky-50/40',
    violet: 'text-violet-700 border-violet-200 bg-violet-50/40',
  }[accent] || '';
  return (
    <Card className={`p-3 border ${cls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </Card>
  );
}

function heatColor(pct) {
  if (!pct) return 'bg-slate-200 dark:bg-slate-700';
  if (pct < 20) return 'bg-emerald-100 dark:bg-emerald-900/40';
  if (pct < 40) return 'bg-emerald-200 dark:bg-emerald-800';
  if (pct < 60) return 'bg-emerald-300 dark:bg-emerald-700';
  if (pct < 80) return 'bg-emerald-400 dark:bg-emerald-600';
  return 'bg-emerald-500 dark:bg-emerald-500';
}

function MonthGrid({ year, month, days }) {
  // Build a 6×7 grid (Pzt-first). `days` is a map "YYYY-MM-DD" → count.
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const dow = (first.getDay() + 6) % 7; // 0 = Mon
    const out = [];
    for (let i = 0; i < dow; i += 1) out.push(null);
    const lastDay = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= lastDay; d += 1) {
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const count = days[iso] || 0;
      // ISO week-of-year for jump links
      const dt = new Date(year, month, d);
      const tmp = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
      const dayNum = tmp.getUTCDay() || 7;
      tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
      const wk = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
      out.push({ d, iso, count, wk, isSunday: ((dt.getDay() + 6) % 7) === 6 });
    }
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [year, month, days]);

  return (
    <div className="p-3">
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase text-slate-500 font-semibold mb-1">
        {DAY_NAMES_SHORT.map((d) => <div key={d} className="text-center px-1 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          return (
            <Link
              key={i}
              to={`/dashboard/planning?year=${year}&week=${c.wk}`}
              className={`group relative aspect-[5/4] rounded border ${
                c.count > 0
                  ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:border-emerald-800'
                  : 'bg-white border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800'
              } ${c.isSunday ? 'opacity-70' : ''} transition-colors flex flex-col items-center justify-center`}
              title={`${c.iso} — ${c.count} atama`}
              data-testid={`planning-summary-day-${c.iso}`}
            >
              <span className={`text-[11px] font-semibold ${c.isSunday ? 'text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
                {c.d}
              </span>
              {c.count > 0 && (
                <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-bold tabular-nums">
                  {c.count}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
